import React, { useMemo } from "react";
import { Panel, Skeleton } from "components/commons";
import lang from "translations";
import { TimeUnit } from "enums";
import { formatNumberWithComma, parseAmountToNumber, toAmount } from "services";
import styles from "../dashboard-charts.module.scss";

const MAX_BAR_HEIGHT = 154;
const MIN_VISIBLE_BAR_HEIGHT = 8;
const SALES_INTERVAL_LABEL = {
  [TimeUnit.Hour]: "hour",
  [TimeUnit.Day]: "day",
  [TimeUnit.Week]: "week",
  [TimeUnit.WeekS]: "week",
  [TimeUnit.Month]: "month",
  [TimeUnit.Quarter]: "quarter",
};

const formatSalesValue = (value) => {
  return Number.isInteger(value) ? formatNumberWithComma(value) : toAmount(value);
};

const SalesOverTimeChart = ({ mappedData = {}, submittedParams = {}, loading, error, retry }) => {
  const interval = submittedParams.interval || TimeUnit.Week;

  const bars = useMemo(() => {
    const chartData = mappedData.data || {};
    const labels = chartData.labels || [];
    const values = chartData.data || [];
    const points = labels.map((label, index) => ({
      label,
      value: parseAmountToNumber(values[index]) || 0,
    }));
    const visiblePoints = interval === TimeUnit.Hour && points.some((point) => point.value > 0)
      ? points.filter((point) => point.value > 0)
      : points;
    const maxValue = Math.max(...visiblePoints.map((point) => point.value), 0);

    return visiblePoints.map(({ label, value }) => {
      const height = maxValue
        ? Math.max((value / maxValue) * MAX_BAR_HEIGHT, value ? MIN_VISIBLE_BAR_HEIGHT : 0)
        : 0;

      return {
        label,
        value,
        height,
      };
    });
  }, [interval, mappedData.data]);

  const legendLabel = useMemo(() => {
    return `Sales per ${SALES_INTERVAL_LABEL[interval] || "period"}`;
  }, [interval]);

  const hasSales = bars.some((bar) => bar.value > 0);

  return (
    <Panel className={styles.salesOverTimeCard} mb="0" parent={false}>
      <div className={styles.salesOverTimeHeader}>
        <span>{lang.salesOverTime}</span>
        <span className={styles.salesOverTimeLegend}>
          <span className={styles.salesOverTimeDot} />
          {legendLabel}
        </span>
      </div>

      {loading && <Skeleton />}

      {!loading && error && (
        <div className={styles.salesOverTimeMessage} onClick={retry}>
          {lang.failedToLoad} <span>{lang.clickHereToRetry}</span>
        </div>
      )}

      {!loading && !error && !hasSales && (
        <div className={styles.salesOverTimeMessage}>{lang.noSalesInDateRange}</div>
      )}

      {!loading && !error && hasSales && (
        <div className={styles.salesOverTimeBars}>
          {bars.map((bar) => (
            <div className={styles.salesOverTimeColumn} key={`${bar.label}-${bar.value}`} tabIndex={0}>
              <div
                className={styles.salesOverTimeBarWrap}
                style={{ "--bar-height": `${bar.height}px` }}
              >
                <div className={styles.salesOverTimeValueLabel}>{formatSalesValue(bar.value)}</div>
                <div className={styles.salesOverTimeBar} />
              </div>
              <div className={styles.salesOverTimeLabel}>{bar.label}</div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default SalesOverTimeChart;
